import { FC, useState } from 'react'
import { Modal } from 'ui/Modal'
import { PaginatedMiniList } from 'ui/components/PaginatedMiniList'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import { usePaymentTerminals } from 'api/PaymentTerminals'
import { WaitingForIdentity } from './WaitingForIdentity'
import { useTranslation } from 'react-i18next'
import { Alert } from 'ui/components/Alert'
import { AssociatedBarcode, PaymentTerminal, TerminalRequest } from '@trybeapp/sdk'
import { Button } from 'ui/components/Button'
import { useAssociatedBarcodeForClient, useGetCustomer } from 'api/Customers'
import { AssociatedBarcodeCard } from './ManageBarcodeModal'
import { Spinner } from 'ui/components/Spinner'
import { Card } from 'ui/components/Card'
import { Text } from 'ui/components/Text'
import { SetExpiryDateModal } from './SetExpiryDateModal'
import { AssociatePaymentMethodModal } from './AssociatePaymentMethodModal'
import { useHistory } from 'react-router-dom'
import { AssociateOrderModal } from './AssociateOrderModal'
import { initials } from 'utilities/StringUtils'

interface IdentityRequestModalProps {
  onClose: () => void
  isOpen: boolean
}

export const IdentityRequestModal: FC<IdentityRequestModalProps> = ({ onClose, isOpen }) => {
  const { t } = useTranslation()

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('global_header.identity_request.modal.title')}
      onClose={handleClose}
    >
      <IdentityRequestContent onComplete={handleClose} />
    </Modal>
  )
}

interface IdentityRequestContentProps {
  onComplete: () => void
}

type CurrentStep = 'select' | 'waiting' | 'result'

const IdentityRequestContent: FC<IdentityRequestContentProps> = ({ onComplete }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const { isLoading, data: { data: terminals = [], meta = {} } = {} } = usePaymentTerminals({
    page,
  })
  const [currentStep, setCurrentStep] = useState<CurrentStep>('select')
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null)
  const [response, setResponse] = useState<TerminalRequest | null>(null)
  const [showSetExpiryModal, setShowSetExpiryModal] = useState(false)
  const [showAssociatePaymentMethodModal, setShowAssociatePaymentMethodModal] = useState(false)
  const [selectedBarcode, setSelectedBarcode] = useState<AssociatedBarcode | null>(null)
  const history = useHistory()

  const {
    isLoading: isLoadingCustomer,
    isSuccess: hasLoadedCustomer,
    data: customerData,
  } = useGetCustomer(response?.processor_data?.customer_id, {
    enabled: currentStep === 'result' && !!response?.processor_data?.customer_id,
  })

  const {
    isLoading: isLoadingBarcode,
    isSuccess: hasLoadedBarcode,
    data: barcodeData,
  } = useAssociatedBarcodeForClient(
    response?.processor_data?.customer_id,
    response?.processor_data?.barcode_id,
    {
      enabled:
        currentStep === 'result' &&
        !!response?.processor_data?.customer_id &&
        !!response?.processor_data?.barcode_id,
    }
  )

  const handleSendRequest = (terminal: PaymentTerminal) => {
    const foundTerminal = terminals.find((t) => t.id === terminal.id)
    if (foundTerminal) {
      setSelectedTerminal(foundTerminal)
      setCurrentStep('waiting')
    }
  }

  const handleIdentityReceived = (data: TerminalRequest) => {
    setResponse(data)
    setCurrentStep('result')
  }

  const handleBackToSelect = () => {
    setCurrentStep('select')
    setSelectedTerminal(null)
  }

  const handleComplete = () => {
    onComplete()
  }

  const navigateToClient = () => {
    if (response?.processor_data?.customer_id) {
      history.push(`/clients/${response.processor_data.customer_id}`)
      onComplete()
    }
  }

  return (
    <>
      {currentStep === 'select' && (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {t('global_header.identity_request.modal.description')}
            </p>
          </div>

          <PaginatedMiniList
            isLoading={isLoading}
            emptyMessage={t('global_header.identity_request.modal.empty_message')}
            meta={meta}
            onPageChange={setPage}
            rows={terminals.map((terminal) => (
              <ResourceListItem
                key={terminal.id}
                title={terminal.name || terminal.id}
                onClick={() => handleSendRequest(terminal)}
                compact
              />
            ))}
          />
        </>
      )}

      {currentStep === 'waiting' && selectedTerminal && (
        <WaitingForIdentity
          terminal={selectedTerminal}
          onIdentityReceived={handleIdentityReceived}
          onCancel={handleBackToSelect}
        />
      )}

      {currentStep === 'result' && (
        <div className="py-4">
          {!response?.processor_data.customer_id && (
            <Alert title={t('global_header.identity_request.result.not_associated_title')}>
              {t('global_header.identity_request.result.not_associated_description')}
            </Alert>
          )}

          {(isLoadingCustomer || isLoadingBarcode) && <Spinner />}

          <div className="space-y-8">
            {hasLoadedCustomer && (
              <div className="space-y-2">
                <Text type="label">
                  {t('global_header.identity_request.result.customer_label')}
                </Text>
                <Card withPadding={false}>
                  <ResourceListItem
                    showDisclosureArrow
                    onClick={navigateToClient}
                    avatar={{
                      url: customerData?.data?.avatar?.url,
                      initials: initials(customerData?.data?.full_name),
                    }}
                    title={
                      customerData?.data?.full_name ||
                      t('global_header.identity_request.result.no_customer_name')
                    }
                    subtitle={
                      customerData?.data?.email ||
                      t('global_header.identity_request.result.no_customer_email')
                    }
                    compact
                  />
                </Card>
              </div>
            )}

            {hasLoadedBarcode && (
              <div className="space-y-2">
                <Text type="label">
                  {t('global_header.identity_request.result.wearable_label')}
                </Text>
                <Card withPadding={false}>
                  <AssociatedBarcodeCard
                    clientId={response?.processor_data.customer_id}
                    barcode={barcodeData?.data}
                    onAssociatePaymentMethod={() => {
                      setSelectedBarcode(barcodeData?.data || null)
                      setShowAssociatePaymentMethodModal(true)
                    }}
                    onShowExpiryModal={() => {
                      setSelectedBarcode(barcodeData?.data || null)
                      setShowSetExpiryModal(true)
                    }}
                    onAssociateOrder={() => {
                      setSelectedBarcode(barcodeData?.data || null)
                      setShowAssociatePaymentMethodModal(true)
                    }}
                  />
                </Card>
              </div>
            )}

            <SetExpiryDateModal
              clientId={response?.processor_data.customer_id!}
              isOpen={showSetExpiryModal}
              onClose={() => {
                setShowSetExpiryModal(false)
                setSelectedBarcode(null)
              }}
              barcode={selectedBarcode}
            />

            {selectedBarcode && (
              <AssociatePaymentMethodModal
                isOpen={showAssociatePaymentMethodModal}
                onClose={() => setShowAssociatePaymentMethodModal(false)}
                clientId={response?.processor_data.customer_id!}
                barcodeId={selectedBarcode.id}
                initialPaymentMethodId={selectedBarcode.payment_method?.id}
              />
            )}

            {selectedBarcode && (
              <AssociateOrderModal
                isOpen={showAssociatePaymentMethodModal}
                onClose={() => setShowAssociatePaymentMethodModal(false)}
                barcodeId={selectedBarcode.id}
                initialOrderId={selectedBarcode.order?.id || undefined}
              />
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <Button label={t('modal_button.close')} variant="primary" onClick={handleComplete} />
          </div>
        </div>
      )}
    </>
  )
}
